// --- API URL ---

export const NGROK_HOST_PATTERN = /ngrok-free\.(dev|app)|\.ngrok\.io|ngrok\.io/i;

/**
 * When `VITE_API_URL` is unset at build time, the client uses this origin (REST + WebSocket).
 * Publication-fork default: clean Worker (never silently fall back to production app.myth-kit.com).
 */
export const DEFAULT_PUBLIC_API_ORIGIN = 'https://mythkit-api-clean.kbruneau1013.workers.dev';

/**
 * Fixes bases like `https://app.example.com/app.example.com` when the host was pasted twice
 * (requests become `/app.example.com/api/...` and typically get 405 from static/SPA routes).
 */
export function normalizeApiBaseUrl(raw: string | undefined): string | undefined {
  if (!raw || typeof raw !== 'string') return undefined;
  const trimmed = raw.trim().replace(/\/$/, '');
  if (!trimmed) return undefined;
  /** Bare `app.example.com` is resolved relative to the page origin and becomes `/app.example.com/...`. */
  let absolute = trimmed;
  if (trimmed.startsWith('//')) {
    absolute = `https:${trimmed}`;
  } else if (!/^[a-z][a-z0-9+.-]*:/i.test(trimmed) && !trimmed.startsWith('/')) {
    absolute = `https://${trimmed}`;
  }
  try {
    const u = new URL(absolute);
    const dupPrefix = `/${u.hostname}`;
    const { pathname } = u;
    if (pathname === dupPrefix || pathname.startsWith(`${dupPrefix}/`)) {
      const rest = pathname === dupPrefix ? '' : pathname.slice(dupPrefix.length);
      const joined = rest ? `${u.origin}${rest}` : u.origin;
      return joined.replace(/\/$/, '');
    }
    return u.href.replace(/\/$/, '');
  } catch {
    return undefined;
  }
}

/** Coerce env, localStorage, or discovery values into a usable API origin (used by REST + WS). */
export function resolveApiBaseUrl(raw: string | null | undefined): string | undefined {
  if (raw == null) return undefined;
  const s = String(raw).trim();
  if (!s) return undefined;
  const n = normalizeApiBaseUrl(s);
  if (n !== undefined && n !== '') return n;
  return s.replace(/\/$/, '') || undefined;
}

export function getEffectiveViteApiUrl(): string | undefined {
  const url = typeof import.meta !== 'undefined' ? import.meta.env?.VITE_API_URL : undefined;
  const normalized = normalizeApiBaseUrl(url);
  if (normalized && !NGROK_HOST_PATTERN.test(normalized)) {
    return normalized;
  }
  return undefined;
}
